import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { getUser } from 'Utils/UserCookie';
import { useStore } from 'Hooks/useStore';
import { filterByEventId, initPaymentIframe } from 'Utils/Payment';
import { attachEventListener, removeEventListener } from 'Utils/Browser';
import { trackFormError } from 'Utils/Analytics';
import { getTaxByZip, resetTax, updateTaxByZip } from 'State/Checkout';

import './PaymentFrame.css';
import { logError } from 'Utils/Logger';
import { getDefaultConfig } from 'Utils/Store';

// payment iframe events names
const PAYMENT_FORM_FIELD_CHANGE = 'PAYMENT_FORM_FIELD_CHANGE';
const TAX_UPDATED = 'TAX_UPDATED';
const VALID_PAYMENT_FORM = 'VALID_PAYMENT_FORM';
const ONFOCUS_DATA = 'ONFOCUS_DATA';

export const PaymentFrame = ({
  onValidate,
  formName,
  onExpiryError,
  resetFormError,
  setErrorMessage,
  updatePaymentServiceId,
}) => {
  const { isPlanSelected, selectedPlan } = useStore('plans');
  const { activeSubscriptionId } = useStore('subscription');
  const user = useStore('user');
  const { profileId } = user;
  const { showCVV, rootDomain, evPaymentFrameUrl } = getDefaultConfig();
  const [, domainName] = rootDomain.split('https://');
  const isCvvRequired = showCVV ? 1 : 0;
  const { isValid, couponCode } = useStore('promoCode');

  const paymentFrameEventListener = useCallback(
    (eventData) => {
      const { eventId, data } = filterByEventId(eventData);

      if (eventId && eventId === ONFOCUS_DATA) {
        // When the form is re-focused to fix errors, clear error. NATCOM-465
        resetFormError();
        if (setErrorMessage) {
          setErrorMessage('');
        }
      }

      if (eventId) {
        if (eventId === PAYMENT_FORM_FIELD_CHANGE) {
          if (!data.isValid) {
            const { errorDescription } = data;
            trackFormError(formName, {
              message: errorDescription,
              status: 1000,
            });
          }

          if (data.fieldName === 'expiryDate') {
            onExpiryError(!data.isValid);
          }
        }

        if (eventId === VALID_PAYMENT_FORM && onValidate) {
          onValidate(data.isValid);
        }

        if (eventId === TAX_UPDATED) {
          const zipCode = data?.GetProrateWithTaxResponseMessage?.billingAddress?.zipCode;
          if (zipCode && couponCode && isValid) {
            // call getProrate again to get new tax with coupon applied
            getTaxByZip(zipCode);
          } else {
            // fall back to default behavior if zip/coupon code are not available
            updateTaxByZip(data);
          }
        }

        if ((!data.isValid && data.errorType) || data.GetProrateWithTaxResponseMessage?.failureMessage) {
          logError(
            new Error(data.errorType || data.GetProrateWithTaxResponseMessage?.failureMessage?.errorMessage),
            'payment',
            { eventId, ...data }
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [couponCode, isValid]
  );

  const createFrame = () => ({
    __html: `<iframe class="EvPaymentFrame" title="Payment Form" name="paymentpage" frameborder="0" scrolling="no" />`,
  });

  let selectedPlanId = '';
  let promotionId = '';
  if (isPlanSelected && selectedPlan) {
    const { planId, promotionID } = selectedPlan;
    selectedPlanId = planId;
    promotionId = promotionID;
  }
  const serviceId = updatePaymentServiceId || selectedPlanId || activeSubscriptionId;

  const [accessToken, setAccessToken] = useState('');
  useEffect(() => {
    getUser().then((userData) => setAccessToken(userData?.accessToken || ''));
  }, []);

  useEffect(() => {
    if (accessToken) {
      attachEventListener('message', paymentFrameEventListener);
      initPaymentIframe();
    }

    return () => {
      removeEventListener('message', paymentFrameEventListener);
      console.debug(`Payment Frame: unmount`);
      resetTax();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionId, serviceId, accessToken, paymentFrameEventListener]);

  return (
    <Fragment>
      <form id="evPaymentIframe" target="paymentpage" action={evPaymentFrameUrl} method="post">
        <input type="hidden" name="serviceId" value={serviceId} />
        <input type="hidden" name="isCvvRequired" value={isCvvRequired} />
        <input type="hidden" name="domainName" value={domainName} />
        <input type="hidden" name="cpCustomerID" value={profileId} />
        <input type="hidden" name="accessToken" value={accessToken} />
        <input type="hidden" name="promotionId" value={promotionId} />
      </form>
      {accessToken ? <div dangerouslySetInnerHTML={createFrame()} className="EvPaymentFrameWrapper" /> : null}
    </Fragment>
  );
};
